/**
 * AgendaView — "By date": every filtered plan in date order, read the way a
 * calendar agenda reads. Months are the strongest heading, each day is
 * stated once in a left gutter (with "Today" / "Tomorrow" / "In 3 days" for
 * the coming week), and the plans sit to its right as two-line rows.
 *
 * Semantics:
 *  - Group key = plannedDate for exact/week/ranged entries; ranged entries
 *    appear ONCE at their start (the row's meta line says "through …").
 *  - Month-precision entries don't fabricate a day — they group under the
 *    month's "Any day" gutter, first in that month.
 *  - Day keys ascend for upcoming/all, DESCEND for past (recent first).
 *  - Rows sort by their visible channel identity, so the order is legible.
 */

import React from 'react';
import { Card, EmptyState } from '../../ui';
import EntryRow from './EntryRow';
import { CalendarEntry, Tentpole } from './types';
import {
  entryDisplayParts,
  monthKeyOf,
  monthLabel,
  plansWord,
  relativeDayLabel,
  todayYmd,
  weekdayShort,
} from './shared';

interface AgendaViewProps {
  /** timeframedEntries from CalendarTab — timeframe already applied. */
  entries: CalendarEntry[];
  tentpoleBySlug: Map<string, Tentpole>;
  timeframe: 'upcoming' | 'past' | 'all';
  onOpenEntry: (id: string) => void;
}

const GUTTER_WIDTH = 64;

const entrySort = (a: CalendarEntry, b: CalendarEntry): number =>
  entryDisplayParts(a).primary.localeCompare(entryDisplayParts(b).primary) ||
  a.creatorLabel.localeCompare(b.creatorLabel) ||
  a.title.localeCompare(b.title);

const AgendaView: React.FC<AgendaViewProps> = ({ entries, tentpoleBySlug, timeframe, onOpenEntry }) => {
  // Split: month-precision entries bucket per month; the rest group per day.
  const byDay: Record<string, CalendarEntry[]> = {};
  const byMonthLoose: Record<string, CalendarEntry[]> = {};
  entries.forEach((e) => {
    if (e.datePrecision === 'month') {
      const key = monthKeyOf(e.plannedDate);
      (byMonthLoose[key] = byMonthLoose[key] || []).push(e);
    } else {
      (byDay[e.plannedDate] = byDay[e.plannedDate] || []).push(e);
    }
  });

  const desc = timeframe === 'past';
  const dayKeys = Object.keys(byDay).sort();
  if (desc) dayKeys.reverse();

  // Months in render order: every month that has a loose bucket or day groups.
  const monthSeen: Record<string, boolean> = {};
  const monthKeys: string[] = [];
  const pushMonth = (key: string) => {
    if (!monthSeen[key]) { monthSeen[key] = true; monthKeys.push(key); }
  };
  Object.keys(byMonthLoose).forEach(pushMonth);
  dayKeys.forEach((d) => pushMonth(monthKeyOf(d)));
  monthKeys.sort();
  if (desc) monthKeys.reverse();

  if (entries.length === 0) {
    return (
      <EmptyState
        title="No plans in this timeframe"
        subtitle="Try widening the timeframe or clearing a filter."
        brand={false}
      />
    );
  }

  const today = todayYmd();
  const summary =
    timeframe === 'upcoming' ? `${plansWord(entries.length)} coming up or in progress`
      : timeframe === 'past' ? `${plansWord(entries.length)} in the past, most recent first`
        : `${plansWord(entries.length)}, all time`;

  const rows = (list: CalendarEntry[]) => list.slice().sort(entrySort).map((e) => (
    <EntryRow
      key={e.id}
      entry={e}
      tentpole={e.tentpoleSlug ? tentpoleBySlug.get(e.tentpoleSlug) : undefined}
      showMoment
      onOpen={onOpenEntry}
    />
  ));

  return (
    <Card>
      <div style={summaryStyle}>{summary}</div>
      {monthKeys.map((month, monthIndex) => {
        const loose = byMonthLoose[month] || [];
        const monthDays = dayKeys.filter((d) => monthKeyOf(d) === month);
        const monthCount = loose.length + monthDays.reduce((n, d) => n + byDay[d].length, 0);
        const groups: Array<{ key: string; gutter: React.ReactNode; list: CalendarEntry[] }> = [];
        if (loose.length > 0) {
          groups.push({
            key: `${month}-any`,
            list: loose,
            gutter: (
              <>
                <div style={gutterMonthStyle}>
                  {new Date(`${month}-01T00:00:00`).toLocaleDateString('en-US', { month: 'short' })}
                </div>
                <div style={gutterWeekdayStyle}>Any day</div>
              </>
            ),
          });
        }
        monthDays.forEach((day) => {
          const isToday = day === today;
          const relative = relativeDayLabel(day);
          groups.push({
            key: day,
            list: byDay[day],
            gutter: (
              <>
                <div style={isToday ? gutterTodayStyle : { ...gutterDayStyle, color: day < today ? 'var(--fg-3)' : 'var(--fg-1)' }}>
                  {Number(day.slice(8, 10))}
                </div>
                <div style={gutterWeekdayStyle}>{weekdayShort(day)}</div>
                {relative && <div style={gutterRelativeStyle}>{relative}</div>}
              </>
            ),
          });
        });
        return (
          <section key={month} aria-label={monthLabel(month)}>
            <div style={{ ...monthHeaderStyle, paddingTop: monthIndex === 0 ? 10 : 26 }}>
              <h3 style={monthTitleStyle}>{monthLabel(month)}</h3>
              <span style={monthCountStyle}>{plansWord(monthCount)}</span>
            </div>
            {groups.map((group, groupIndex) => (
              <div
                key={group.key}
                style={{ ...dayGroupStyle, borderTop: groupIndex === 0 ? 'none' : '1px solid var(--border-subtle)' }}
              >
                <div style={gutterStyle}>{group.gutter}</div>
                <div style={{ minWidth: 0 }}>{rows(group.list)}</div>
              </div>
            ))}
          </section>
        );
      })}
    </Card>
  );
};

const summaryStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--fg-3)',
  fontFamily: 'var(--font-sans)',
};

/** Sticks while its month scrolls by; bleeds into the Card's padding so rows
 *  passing underneath never show beside it. */
const monthHeaderStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 'var(--z-sticky)',
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
  margin: '0 calc(-1 * var(--space-5))',
  padding: '0 var(--space-5) 10px',
  background: 'var(--bg-surface-1)',
  borderBottom: '1px solid var(--border-1)',
};

const monthTitleStyle: React.CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display)',
  fontSize: 18,
  fontWeight: 700,
  letterSpacing: '-0.01em',
  color: 'var(--fg-1)',
};

const monthCountStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--fg-3)',
  fontFamily: 'var(--font-sans)',
};

const dayGroupStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: `${GUTTER_WIDTH}px minmax(0, 1fr)`,
  columnGap: 12,
  padding: '6px 0',
};

const gutterStyle: React.CSSProperties = {
  paddingTop: 10,
  fontFamily: 'var(--font-sans)',
};

const gutterDayStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: 22,
  fontWeight: 700,
  lineHeight: 1,
  fontVariantNumeric: 'tabular-nums',
};

const gutterTodayStyle: React.CSSProperties = {
  ...gutterDayStyle,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 34,
  height: 34,
  marginTop: -6,
  marginLeft: -6,
  borderRadius: 'var(--radius-pill)',
  background: 'var(--accent)',
  color: 'var(--fg-on-accent)',
  fontSize: 18,
};

const gutterMonthStyle: React.CSSProperties = {
  ...gutterDayStyle,
  fontSize: 18,
  color: 'var(--fg-1)',
};

const gutterWeekdayStyle: React.CSSProperties = {
  marginTop: 5,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 'var(--track-eyebrow)',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
};

const gutterRelativeStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 11.5,
  fontWeight: 600,
  lineHeight: 1.25,
  color: 'var(--accent)',
};

export default AgendaView;
